/**
 * Browser Position Monitor
 * Global monitor that enforces position limits independently of trade batches
 * 
 * Features:
 * - Event-driven: Listens to real-time WebSocket position updates (instant response)
 * - Backup polling: 30-second safety net if events are missed
 * - Uses BrowserPositionTracker data (no API calls, zero rate limiting)
 * - Cancels excess orders when position limits exceeded
 * - Works independently of Auto Trade Bot batch processing
 * - Prevents race conditions where positions fill faster than bot can react
 * - Always logs current state for visibility
 */
class BrowserPositionMonitor {
    constructor() {
        this.monitorInterval = null;
        this.isRunning = false;
        this.pollIntervalMs = 2000; // 2 seconds (backup only, events are primary)
        this.lastCheckTimestamp = 0;
        this.eventHandler = null;
        
        // console.log('[POSITION-MONITOR] 🎯 Position Monitor initialized');
    }
    
    /**
     * Start the global position limit monitor
     */
    start() {
        if (this.isRunning) {
            // console.log('[POSITION-MONITOR] ⚠️ Monitor already running');
            return;
        }
        
        // console.log('[POSITION-MONITOR] 🔄 Starting event-driven position limit monitor...');
        this.isRunning = true;
        
        // Initial check
        this.checkPositionLimits();
        
        // PRIMARY: Listen to real-time position updates from WebSocket
        this.eventHandler = () => this.checkPositionLimits();
        window.addEventListener('positions-update', this.eventHandler);
        
        // BACKUP: 30-second polling as safety net
        this.monitorInterval = setInterval(() => {
            this.checkPositionLimits();
        }, this.pollIntervalMs);
    }
    
    /**
     * Stop the monitor
     */
    stop() {
        if (this.eventHandler) {
            window.removeEventListener('positions-update', this.eventHandler);
            this.eventHandler = null;
        }
        
        if (this.monitorInterval) {
            clearInterval(this.monitorInterval);
            this.monitorInterval = null;
        }
        
        this.isRunning = false;
        // console.log('[POSITION-MONITOR] 🛑 Monitor stopped');
    }
    
    /**
     * Check position limits and cancel excess orders
     */
    async checkPositionLimits() {
        try {
            this.lastCheckTimestamp = Date.now();
            
            // Check 1: Position tracker running?
            const tracker = window.BrowserPositionTracker;
            if (!tracker || !tracker.isRunning) {
                // console.log('[POSITION-MONITOR] ⏸️ Position tracker not running - skipping check');
                return;
            }
            
            // Check 2: Auto Trade Bot enabled?
            const bot = window.BrowserAutoTradeBot;
            if (!bot || !bot.isEnabled()) {
                // console.log('[POSITION-MONITOR] ⏸️ Auto-trade bot not enabled - skipping check');
                return;
            }
            
            // Get current mode settings
            const currentSettings = bot.getCurrentModeSettings();
            const mode = bot.mode || 'easy';
            
            // Get FRESH positions from tracker's internal state (NOT cache)
            const positions = Array.from(tracker.positions.values());
            
            // Count ACTIVE positions only (positions with actual size)
            const activeLongs = positions.filter(pos => {
                const size = parseFloat(pos.size || 0);
                return size > 0;
            }).length;
            
            const activeShorts = positions.filter(pos => {
                const size = parseFloat(pos.size || 0);
                return size < 0;
            }).length;
            
            // ALWAYS LOG - shows monitor is working and current state
            // console.log(`[POSITION-MONITOR] 🔄 ${mode} mode: ${activeLongs}/${currentSettings.maxLongs} longs, ${activeShorts}/${currentSettings.maxShorts} shorts`);
            
            // Check if limits exceeded (at or over limit)
            // This catches cases where positions filled after last check
            if (activeLongs >= currentSettings.maxLongs || activeShorts >= currentSettings.maxShorts) {
                // console.log(`[POSITION-MONITOR] ⚠️ Position limits EXCEEDED - cancelling excess orders`);
                // console.log(`[POSITION-MONITOR]   Longs: ${activeLongs}/${currentSettings.maxLongs}${activeLongs > currentSettings.maxLongs ? ' (OVER)' : ' (AT LIMIT)'}`);
                // console.log(`[POSITION-MONITOR]   Shorts: ${activeShorts}/${currentSettings.maxShorts}${activeShorts > currentSettings.maxShorts ? ' (OVER)' : ' (AT LIMIT)'}`);
                
                // Use existing cancellation logic from auto-trade bot
                await bot.cancelExcessOrders(activeLongs, activeShorts, currentSettings);
                
                // console.log('[POSITION-MONITOR] ✅ Excess order cancellation complete');
            }
            
        } catch (error) {
            // console.error('[POSITION-MONITOR] ❌ Error during position limit check:', error);
            // console.error('[POSITION-MONITOR]   Error details:', error.message);
            // console.error('[POSITION-MONITOR]   Stack:', error.stack);
        }
    }
    
    /**
     * Get monitor status
     */
    getStatus() {
        return {
            isRunning: this.isRunning,
            pollIntervalMs: this.pollIntervalMs,
            lastCheckTimestamp: this.lastCheckTimestamp,
            timeSinceLastCheck: this.lastCheckTimestamp ? Date.now() - this.lastCheckTimestamp : null
        };
    }
}

// Create singleton instance
window.BrowserPositionMonitor = new BrowserPositionMonitor();

// Export for ES6 modules
export { BrowserPositionMonitor };
